import OficinaVirtualDetalle from '../../../../components/OficinaVirtualDetalle';
// DetallesCitaClientePage.tsx — Rediseño sistema Co Marca (igual que AboutusProjects / MiDashboard)
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchCitaById, fetchEspacioServicios, fetchPrecioPlanesServicios, fetchReservaReport,
    Cita, PrecioPlanServicio, EspacioServicio, ReservationReport
} from '../../../../services/citasService';
import { fetchCategories, Categoria } from '../../../../services/citasService';
import {
    Clock, MapPin, Banknote, ArrowLeft, User, Tag, Calendar, Info,
    CheckCircle, AlertCircle, XCircle, PlayCircle,
    DollarSign, Layers, Hash, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';
import ReservationReportSummary from './ReservationReportSummary';
import FacturaCliente from './FacturaCliente';

// ── Design tokens Co Marca ──
const colors = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#FFD97C',
    accentDark: '#EA8406',
};

// ── Section animation wrapper ──
function Section({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
    return (
        <div style={{ animation: `detalleFadeUp 0.6s cubic-bezier(.16,1,.3,1) ${delay}s both` }}>
            {children}
        </div>
    );
}

// ── Info Row ──
function InfoRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
    const { isDark } = useTheme();
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '0.68rem', fontWeight: '700', color: isDark ? '#94a3b8' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                {label}
            </span>
            <span style={{
                fontSize: '0.88rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a',
                fontFamily: mono ? 'monospace' : "'DM Sans', sans-serif",
            }}>
                {value}
            </span>
        </div>
    );
}

// ── Info Card ──
function InfoCard({
    icon: Icon, title, accent, children,
}: {
    icon: any; title: string; accent: string; children: React.ReactNode;
}) {
    const { isDark } = useTheme();
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: isDark ? 'rgba(22, 10, 43, 0.88)' : '#fff',
                borderRadius: '18px',
                border: hovered 
                    ? `1.5px solid ${accent}60` 
                    : (isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9'),
                boxShadow: hovered 
                    ? (isDark ? `0 12px 36px rgba(0,0,0,0.6), 0 0 20px ${accent}25` : `0 12px 36px ${accent}10, 0 2px 12px rgba(0,0,0,0.04)`) 
                    : (isDark ? '0 4px 20px rgba(0,0,0,0.35)' : '0 2px 12px rgba(0,0,0,0.03)'),
                transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
                transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                backdropFilter: 'blur(12px)',
                overflow: 'visible',
            }}
        >
            {/* Barra acento top */}
            <div style={{
                height: '3px',
                background: hovered
                    ? `linear-gradient(90deg, ${accent}, ${accent}40)`
                    : `linear-gradient(90deg, ${accent}40, ${accent}10)`,
                borderRadius: '18px 18px 0 0',
                transition: 'background 0.3s',
            }} />
            <div style={{ padding: '18px 20px' }}>
                {/* Header card */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{
                        width: '34px', height: '34px', borderRadius: '10px',
                        background: hovered ? accent : (isDark ? `${accent}25` : `${accent}12`),
                        border: `1.5px solid ${hovered ? accent : accent + '30'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.3s',
                        flexShrink: 0,
                    }}>
                        <Icon size={16} color={hovered ? '#fff' : accent} strokeWidth={2} />
                    </div>
                    <span style={{
                        fontSize: '0.82rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a',
                        letterSpacing: '-0.01em',
                    }}>
                        {title}
                    </span>
                </div>
                {children}
            </div>
        </div>
    );
}

// ── Estado badge ──
function EstadoBadge({ estado }: { estado: string }) {
    const map: Record<string, { label: string; color: string; Icon: any }> = {
        confirmada:  { label: 'Confirmada',   color: colors.secondaryLight, Icon: CheckCircle },
        activa:      { label: 'Activa',        color: colors.secondary,      Icon: PlayCircle },
        pendiente:   { label: 'Pendiente',     color: colors.accentDark,     Icon: AlertCircle },
        cancelada:   { label: 'Cancelada',     color: colors.primary,        Icon: XCircle },
        completada:  { label: 'Completada',    color: '#64748b',             Icon: CheckCircle },
    };
    const key = estado?.toLowerCase() ?? '';
    const cfg = map[key] ?? { label: estado || 'Confirmada', color: colors.secondary, Icon: CheckCircle };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: `${cfg.color}12`, border: `1.5px solid ${cfg.color}28`,
            borderRadius: '100px', padding: '5px 14px',
            fontSize: '0.72rem', fontWeight: '800', color: cfg.color,
            letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
            <cfg.Icon size={12} strokeWidth={2.5} />
            {cfg.label}
        </span>
    );
}

// ══════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════
const DetallesCitaClientePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [cita, setCita] = useState<Cita | null>(null);
    const [plan, setPlan] = useState<PrecioPlanServicio | null>(null);
    const [espacio, setEspacio] = useState<EspacioServicio | null>(null);
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [report, setReport] = useState<ReservationReport | null>(null);
    const [reportError, setReportError] = useState('');
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const { isDark } = useTheme();

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const encontrada = await fetchCitaById(Number(id));
                setCita(encontrada);
                const [planes, espacios, categoriasData] = await Promise.all([
                    fetchPrecioPlanesServicios(), fetchEspacioServicios(), fetchCategories()
                ]);
                setPlan(planes.find(p => p.id === encontrada.precio_plan_servicio) || null);
                setEspacio(espacios.find(e => e.id === encontrada.espacio_servicio) || null);
                setCategorias(categoriasData);
                try {
                    setReport(await fetchReservaReport(Number(id)));
                    setReportError('');
                } catch (reportRequestError) {
                    console.error('Error cargando reporte de reserva:', reportRequestError);
                    setReport(null);
                    setReportError('No se pudo cargar el reporte financiero de esta reserva.');
                }
            } catch (error) {
                console.error('Error cargando datos:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [id]);

    const getCategoriaNombre = (categoriaId: number | null): string => {
        if (!categoriaId) return 'Sin categoría';
        const categoria = categorias.find(cat => cat.id === categoriaId);
        return categoria ? categoria.nombre : `Categoría #${categoriaId}`;
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'Fecha no disponible';
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString('es-ES', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
        });
    };

    const formatDateShort = (dateString: string | null | undefined) => {
        if (!dateString) return '—';
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString('es-ES', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", background: 'transparent', minHeight: '100vh' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; }
                @keyframes blobFloat {
                    0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
                    50%       { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
                }
                @keyframes detalleFadeUp {
                    from { opacity: 0; transform: translateY(22px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulseD {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(193,52,139,0.35); }
                    50%       { box-shadow: 0 0 0 5px rgba(193,52,139,0); }
                }
                .blob-det { animation: blobFloat 9s ease-in-out infinite; }
                .det-back-btn:hover { background: ${colors.secondary}12 !important; color: ${colors.secondary} !important; border-color: ${colors.secondary}25 !important; }
                .det-ses-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(193,52,139,0.45) !important; }
            `}</style>

            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(16px,3vw,28px)' }}>

                {/* ══ HERO HEADER ══ */}
                <Section>
                    <div style={{
                        position: 'relative', overflow: 'hidden',
                        borderRadius: '28px',
                        background: isDark
                            ? 'linear-gradient(135deg, rgba(22, 10, 43, 0.92) 0%, rgba(32, 12, 58, 0.85) 50%, rgba(44, 15, 78, 0.9) 100%)'
                            : 'linear-gradient(135deg, #ffffff 0%, #faf6fd 50%, #f4e8f9 100%)',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid rgba(97, 37, 123, 0.12)',
                        padding: 'clamp(28px,4vw,44px) clamp(20px,4vw,40px)',
                        marginBottom: '24px',
                        boxShadow: isDark ? '0 12px 36px rgba(0,0,0,0.4), 0 0 24px rgba(193,52,139,0.1)' : '0 4px 32px rgba(97,37,123,0.06)',
                        backdropFilter: 'blur(16px)',
                    }}>
                        {/* Blob decorativo */}
                        <div className="blob-det" style={{
                            position: 'absolute', top: '-80px', right: '-80px',
                            width: '340px', height: '340px', pointerEvents: 'none',
                            background: isDark
                                ? `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}15)`
                                : `linear-gradient(135deg, ${colors.primary}10, ${colors.secondary}08)`,
                            filter: 'blur(30px)',
                        }} />
                        {/* Puntos decorativos */}
                        <div style={{
                            position: 'absolute', bottom: '20px', left: '3%', opacity: isDark ? 0.15 : 0.07,
                            display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '12px',
                            pointerEvents: 'none',
                        }}>
                            {[...Array(25)].map((_, i) => (
                                <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: isDark ? '#c1348b' : colors.primaryDark }} />
                            ))}
                        </div>

                        <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '24px' }}>
                            {/* Left */}
                            <div>
                                {/* Back button */}
                                <button
                                    className="det-back-btn"
                                    onClick={() => navigate('/mis-citas')}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '7px',
                                        background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                                        border: isDark ? '1.5px solid rgba(255, 255, 255, 0.1)' : '1.5px solid #f1f5f9',
                                        borderRadius: '10px', padding: '7px 14px',
                                        fontSize: '0.8rem', fontWeight: '700',
                                        color: isDark ? '#cbd5e1' : '#64748b',
                                        cursor: 'pointer', marginBottom: '16px',
                                        transition: 'all 0.2s', fontFamily: "'DM Sans', sans-serif",
                                    }}
                                >
                                    <ArrowLeft size={14} />
                                    Volver a mis citas
                                </button>

                                {/* Badge Co Marca */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '7px',
                                        background: isDark ? 'rgba(193, 52, 139, 0.2)' : `${colors.primary}12`,
                                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.4)' : `1.5px solid ${colors.primary}25`,
                                        borderRadius: '100px', padding: '5px 14px',
                                        fontSize: '0.7rem', fontWeight: '800',
                                        color: isDark ? '#ffd97c' : colors.primaryDark,
                                        letterSpacing: '0.1em', textTransform: 'uppercase',
                                    }}>
                                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.secondary, animation: 'pulseD 2s infinite' }} />
                                        Co Marca Group
                                    </span>
                                    {cita && <EstadoBadge estado={cita.estado || 'confirmada'} />}
                                </div>

                                <h1 style={{
                                    fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: '900',
                                    color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.05, marginBottom: '8px',
                                }}>
                                    {cita?.reglas_plan_reserva?.is_event ? (
                                        <>Evento: <span style={{ color: isDark ? '#ffd97c' : colors.primary }}>{cita?.reglas_plan_reserva?.evento_titulo}</span></>
                                    ) : (
                                        <>{loading ? 'Detalles de la ' : (cita ? getCategoriaNombre(cita.categoria) + ' — ' : '')}
                                        <span style={{ color: isDark ? '#ffd97c' : colors.primary }}>Cita</span></>
                                    )}
                                </h1>

                                {cita && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                        <Hash size={13} color={isDark ? '#94a3b8' : '#94a3b8'} />
                                        <span style={{
                                            fontSize: '0.82rem', fontWeight: '700', color: isDark ? '#cbd5e1' : '#94a3b8',
                                            background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                                            border: isDark ? '1px solid rgba(193, 52, 139, 0.25)' : '1px solid #f1f5f9',
                                            borderRadius: '8px', padding: '3px 10px', fontFamily: 'monospace',
                                        }}>
                                            {cita.codigo}
                                        </span>
                                    </div>
                                )}

                                {/* Barra de marca */}
                                <div style={{ display: 'flex', gap: '5px', marginTop: '14px' }}>
                                    {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                                        <div key={i} style={{ height: '3px', width: i === 0 ? '28px' : '10px', borderRadius: '3px', background: c }} />
                                    ))}
                                </div>
                            </div>

                            {/* Right: stats badge */}
                            {!loading && cita && (
                                <div style={{
                                    display: 'flex',
                                    background: isDark ? 'rgba(15, 7, 28, 0.88)' : '#fff',
                                    borderRadius: '18px',
                                    border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9',
                                    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 16px rgba(0,0,0,0.04)',
                                    overflow: 'hidden', flexShrink: 0,
                                }}>
                                    {[
                                        { value: `Bs ${Number(cita.total) || 0}`, label: 'Total',    color: isDark ? '#ffd97c' : colors.primary },
                                        { value: `${cita.cantidad_periodos ?? '—'}`, label: 'Periodos', color: isDark ? '#f472b6' : colors.secondary },
                                        { value: formatDateShort(cita.fecha_inicio), label: 'Inicio',   color: colors.primaryDark },
                                    ].map((s, i) => (
                                        <div key={s.label} style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            padding: '16px 22px',
                                            borderRight: i < 2 ? (isDark ? '1.5px solid rgba(255,255,255,0.06)' : '1.5px solid #f1f5f9') : 'none',
                                        }}>
                                            <div style={{ fontSize: '1.25rem', fontWeight: '900', color: s.color, lineHeight: 1, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{s.value}</div>
                                            <div style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '600', marginTop: '4px' }}>{s.label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Section>

                {/* ══ BODY ══ */}
                {loading ? (
                    <Section delay={0.05}>
                        <div style={{
                            background: '#fff', borderRadius: '24px', border: '1.5px solid #f1f5f9',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            padding: '80px 20px', gap: '16px',
                        }}>
                            <div style={{
                                width: '48px', height: '48px', borderRadius: '50%',
                                border: `3px solid ${colors.primary}20`,
                                borderTop: `3px solid ${colors.primary}`,
                                animation: 'spin 0.7s linear infinite',
                            }} />
                            <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#94a3b8' }}>Cargando detalles...</p>
                        </div>
                    </Section>
                ) : !cita ? (
                    <Section delay={0.05}>
                        <div style={{
                            display: 'flex', alignItems: 'flex-start', gap: '14px',
                            background: `${colors.accentDark}08`, border: `1.5px solid ${colors.accentDark}25`,
                            borderLeft: `4px solid ${colors.accentDark}`,
                            borderRadius: '18px', padding: '22px',
                        }}>
                            <div style={{
                                width: '38px', height: '38px', borderRadius: '11px', flexShrink: 0,
                                background: `${colors.accentDark}14`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Info size={18} color={colors.accentDark} />
                            </div>
                            <div>
                                <p style={{ fontSize: '0.95rem', fontWeight: '800', color: colors.accentDark, marginBottom: '4px' }}>Cita no encontrada</p>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>La cita solicitada no existe o ha sido cancelada.</p>
                            </div>
                        </div>
                    </Section>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: '20px', alignItems: 'start' }}>

                        {/* ── Columna izquierda ── */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {/* Card: Servicio */}
                            <Section delay={0.06}>
                                <InfoCard icon={Tag} title="Detalles del Servicio" accent={colors.primary}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
                                        <InfoRow label="Plan contratado" value={plan ? `${plan.servicio_nombre} — ${plan.plan_nombre}` : 'No disponible'} />
                                        <InfoRow label="Espacio" value={espacio?.espacio_nombre || 'No asignado'} />
                                        <InfoRow label="Categoría" value={getCategoriaNombre(cita.categoria)} />
                                        <InfoRow label="Duración" value={`${cita.cantidad_periodos ?? 'N/A'} periodos`} />
                                    </div>
                                </InfoCard>
                            </Section>

                            {/* Cards: Fechas lado a lado */}
                            <Section delay={0.1}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    {/* Inicio */}
                                    <InfoCard icon={Calendar} title="Fecha de inicio" accent={colors.secondary}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            <p style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.4, textTransform: 'capitalize' }}>
                                                {formatDate(cita.fecha_inicio)}
                                            </p>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                <div style={{
                                                    width: '26px', height: '26px', borderRadius: '8px',
                                                    background: `${colors.secondary}10`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <Clock size={13} color={colors.secondary} />
                                                </div>
                                                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: colors.secondary }}>
                                                    {cita.hora_inicio}
                                                </span>
                                            </div>
                                        </div>
                                    </InfoCard>

                                    {/* Fin */}
                                    {cita.fecha_fin ? (
                                        <InfoCard icon={Calendar} title="Fecha de finalización" accent={colors.secondaryLight}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <p style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.4, textTransform: 'capitalize' }}>
                                                    {formatDate(cita.fecha_fin)}
                                                </p>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                    <div style={{
                                                        width: '26px', height: '26px', borderRadius: '8px',
                                                        background: `${colors.secondaryLight}10`,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <Clock size={13} color={colors.secondaryLight} />
                                                    </div>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: colors.secondaryLight }}>
                                                        {cita.hora_fin}
                                                    </span>
                                                </div>
                                            </div>
                                        </InfoCard>
                                    ) : (
                                        <div style={{
                                            background: '#f8fafc', borderRadius: '18px', border: '1.5px dashed #e2e8f0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <p style={{ fontSize: '0.78rem', color: '#cbd5e1', fontWeight: '600' }}>Sin fecha de fin</p>
                                        </div>
                                    )}
                                </div>
                            </Section>

                            {/* Card: Ubicación (si existe) */}
                            {espacio && (
                                <Section delay={0.13}>
                                    <InfoCard icon={MapPin} title="Ubicación del Espacio" accent={colors.accentDark}>
                                        <InfoRow label="Nombre del espacio" value={espacio.espacio_nombre} />
                                    </InfoCard>
                                </Section>
                            )}

                            <FacturaCliente key={`${user?.id}-${cita.id}`} citaId={cita.id}/>

                            {report && (
                                <Section delay={0.15}>
                                    <ReservationReportSummary report={report} oficinaVirtual={cita.oficina_virtual} />
                                </Section>
                            )}
                            {!report && cita.oficina_virtual && <OficinaVirtualDetalle expediente={cita.oficina_virtual} />}
                            {reportError && (
                                <div role="alert" style={{ padding: '14px 16px', borderRadius: '12px', background: '#fff1f4', color: '#9B004A', fontWeight: 700, fontSize: '.82rem' }}>
                                    {reportError}
                                </div>
                            )}
                        </div>

                        {/* ── Columna derecha ── */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {/* Card: Pago */}
                            <Section delay={0.08}>
                                <InfoCard icon={Banknote} title="Resumen de Pago" accent={colors.secondaryLight}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: '600' }}>Subtotal</span>
                                            <span style={{ fontSize: '0.88rem', fontWeight: '700', color: '#374151' }}>
                                                {Number(cita.subtotal) || 0} Bs.
                                            </span>
                                        </div>

                                        {cita.descuento_directo && Number(cita.descuento_directo) > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: '0.82rem', color: colors.secondaryLight, fontWeight: '600' }}>Descuento</span>
                                                <span style={{ fontSize: '0.88rem', fontWeight: '700', color: colors.secondaryLight }}>
                                                    -{Number(cita.descuento_directo)} Bs.
                                                </span>
                                            </div>
                                        )}

                                        <div style={{ height: '1px', background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9', margin: '4px 0' }} />

                                        {/* Total highlight */}
                                        <div style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            background: isDark ? 'rgba(193, 52, 139, 0.15)' : 'linear-gradient(135deg, rgba(97, 37, 123, 0.08), rgba(193, 52, 139, 0.05))',
                                            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : '1.5px solid rgba(97, 37, 123, 0.2)',
                                            borderRadius: '12px', padding: '12px 14px',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                <DollarSign size={15} color={isDark ? '#ffd97c' : colors.primary} />
                                                <span style={{ fontSize: '0.82rem', fontWeight: '800', color: isDark ? '#ffd97c' : colors.primaryDark }}>Total</span>
                                            </div>
                                            <span style={{
                                                fontSize: '1.3rem', fontWeight: '900', color: isDark ? '#ffd97c' : colors.primary,
                                                letterSpacing: '-0.025em',
                                            }}>
                                                {Number(cita.total) || 0} <span style={{ fontSize: '0.7rem', fontWeight: '700' }}>Bs.</span>
                                            </span>
                                        </div>
                                    </div>
                                </InfoCard>
                            </Section>

                            {/* Card: Cliente */}
                            <Section delay={0.12}>
                                <InfoCard icon={User} title="Datos del Cliente" accent={colors.secondary}>
                                    {user ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {/* Avatar initials */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                                <div style={{
                                                    width: '42px', height: '42px', borderRadius: '14px',
                                                    background: `linear-gradient(135deg, ${colors.primaryDark}, ${colors.primary})`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#fff', fontSize: '1rem', fontWeight: '900',
                                                    flexShrink: 0,
                                                }}>
                                                    {(user.first_name?.[0] ?? '') + (user.last_name?.[0] ?? '')}
                                                </div>
                                                <div>
                                                    <p style={{ fontSize: '0.9rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: 1.2 }}>
                                                        {user.first_name} {user.last_name}
                                                    </p>
                                                    <p style={{ fontSize: '0.75rem', color: isDark ? '#38bdf8' : colors.secondary, fontWeight: '600' }}>
                                                        {user.email}
                                                    </p>
                                                </div>
                                            </div>

                                            <div style={{ height: '1px', background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9' }} />

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                {user.telefono && <InfoRow label="Teléfono" value={user.telefono} />}
                                                {user.ci_nit && <InfoRow label="CI / NIT" value={user.ci_nit} />}
                                            </div>

                                            <div style={{ height: '1px', background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9' }} />

                                            {/* Declaración Jurada Status */}
                                            <div style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                background: (user as any).declaracion_jurada_firmada 
                                                    ? (isDark ? 'rgba(22, 163, 74, 0.16)' : '#f0fdf4') 
                                                    : (isDark ? 'rgba(217, 119, 6, 0.16)' : '#fffbeb'),
                                                border: (user as any).declaracion_jurada_firmada 
                                                    ? (isDark ? '1px solid rgba(34, 197, 94, 0.35)' : '1px solid #bbf7d0') 
                                                    : (isDark ? '1px solid rgba(245, 158, 11, 0.35)' : '1px solid #fde68a'),
                                                borderRadius: '10px', padding: '8px 12px'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <CheckCircle size={14} color={(user as any).declaracion_jurada_firmada ? (isDark ? '#4ade80' : '#16a34a') : (isDark ? '#fbbf24' : '#d97706')} />
                                                    <span style={{ fontSize: '0.75rem', fontWeight: '800', color: (user as any).declaracion_jurada_firmada ? (isDark ? '#4ade80' : '#166534') : (isDark ? '#fbbf24' : '#92400e') }}>
                                                        {(user as any).declaracion_jurada_firmada ? 'Declaración Jurada Firmada' : 'Declaración Jurada Pendiente'}
                                                    </span>
                                                </div>
                                                {(user as any).fecha_declaracion_jurada && (
                                                    <span style={{ fontSize: '0.68rem', color: isDark ? '#bbf7d0' : '#166534', fontWeight: '600' }}>
                                                        {new Date((user as any).fecha_declaracion_jurada).toLocaleDateString('es-ES')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <p style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: '500' }}>
                                            Información del cliente no disponible
                                        </p>
                                    )}
                                </InfoCard>
                            </Section>

                            {/* Botón Ver Sesiones */}
                            <Section delay={0.16}>
                                <button
                                    className="det-ses-btn"
                                    onClick={() => navigate(`/mis-citas/${cita.id}/sesiones`)}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                        padding: '14px 20px', borderRadius: '16px',
                                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                        border: 'none', color: '#fff',
                                        fontSize: '0.9rem', fontWeight: '800',
                                        cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                        boxShadow: '0 6px 20px rgba(97, 37, 123, 0.35)',
                                        transition: 'all 0.25s cubic-bezier(.16,1,.3,1)',
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    <Layers size={17} strokeWidth={2} />
                                    Ver Sesiones
                                    <ExternalLink size={14} strokeWidth={2} style={{ opacity: 0.75 }} />
                                </button>
                            </Section>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DetallesCitaClientePage;
